import { FC } from 'react';
import { classifyDeltaPct } from '@leopart/calc';
import KpiCard, { IKpiDelta } from '../../../shared/KpiCard/KpiCard';
import { ITrainingMixCompare, ITrainingMixKpi } from '../../../models/allenamenti';
import { pluralizeIt } from '../allenamenti.constants';
import { formatLocalDateIt, formatMinutes, formatPercent } from './analysis.constants';
import styles from './AnalysisKpiRow.module.scss';

interface IAnalysisKpiRowProps {
  kpi: ITrainingMixKpi;
  /** Confronto col periodo precedente (`?compare=1`), se disponibile. */
  compare?: ITrainingMixCompare;
  bySlug: Map<string, { labelIt: string; color: string }>;
}

/**
 * Chip di variazione per i minuti pianificati. Il tono lo decide @leopart/calc
 * (soglie di dominio, non stampate qui): |Δ|>15% attenzione, >50% alert — così
 * un salto di carico salta all'occhio prima di diventare sovraccarico.
 */
const buildMinutesDelta = (compare?: ITrainingMixCompare): IKpiDelta | undefined => {
  if (!compare || compare.deltaPct == null) return undefined;
  const status = classifyDeltaPct(compare.deltaPct);
  return {
    pct: Math.round(compare.deltaPct),
    tone: status === 'neutral' ? 'neutral' : status === 'warn' ? 'warn' : 'alert',
    hint: `vs ${compare.period.label} (${formatMinutes(compare.totalMinutes)})`,
  };
};

/**
 * Riga KPI del periodo — usa shared/KpiCard (promossa), non un quarto stile
 * (design M2/M3 §1.4). `avgMinutesPerSession`/`dominant` sono `null`-aware:
 * "nessun dato" resta "—", mai "0".
 */
const AnalysisKpiRow: FC<IAnalysisKpiRowProps> = ({ kpi, compare, bySlug }) => {
  const dominantLabel = kpi.dominant
    ? (bySlug.get(kpi.dominant.key)?.labelIt ?? kpi.dominant.key)
    : '—';
  const dominantSub = kpi.dominant
    ? `${formatMinutes(kpi.dominant.minutes)} · ${formatPercent(kpi.dominant.percentage)}`
    : 'Nessun dato nel periodo';

  const minutesDelta = buildMinutesDelta(compare);

  return (
    <div className={styles.row}>
      <KpiCard
        label="Minuti pianificati"
        value={formatMinutes(kpi.totalMinutes)}
        sub={`${kpi.totalBlocks} ${pluralizeIt(kpi.totalBlocks, 'blocco', 'blocchi')}`}
        delta={minutesDelta}
      />
      <KpiCard
        label="Sedute"
        value={String(kpi.sessions)}
        sub={`su ${kpi.totalBlocks} ${pluralizeIt(kpi.totalBlocks, 'blocco', 'blocchi')}`}
      />
      <KpiCard
        label="Media min/seduta"
        value={kpi.avgMinutesPerSession.value != null ? `${kpi.avgMinutesPerSession.value} min` : '—'}
        sub={`su ${kpi.avgMinutesPerSession.base} ${pluralizeIt(kpi.avgMinutesPerSession.base, 'seduta', 'sedute')}`}
      />
      <KpiCard label="Categoria dominante" value={dominantLabel} sub={dominantSub} />
      <KpiCard
        label="Blocco più lungo"
        value={kpi.longestBlock ? formatMinutes(kpi.longestBlock.minutes) : '—'}
        sub={
          kpi.longestBlock
            ? `${kpi.longestBlock.title} · ${formatLocalDateIt(kpi.longestBlock.localDate)}`
            : 'Nessun blocco nel periodo'
        }
      />
    </div>
  );
};

export default AnalysisKpiRow;
